import { apiGet, apiPost } from '../../../adapters/api-client/api';

export type DisputeStatus = 'OPEN' | 'ESCALATED' | 'RESOLVED_RELEASED' | 'RESOLVED_REFUNDED';
export type SlaStatus = 'ON_TRACK' | 'AT_RISK' | 'BREACHED' | 'ESCALATED' | 'RESOLVED';

export interface SlaView {
  slaStatus: SlaStatus;
  slaDueAt?: string;
  hoursRemaining?: number;
}

export interface DisputeQueueRow {
  disputeId: string;
  enrollmentId: string;
  milestoneId: string;
  milestoneSequence?: number;
  status: DisputeStatus;
  reason: string;
  openedAt: string;
  sla: SlaView;
  courseId: string;
  courseTitle?: string;
  parent: { userId: string; name?: string };
  teacher: { userId: string; name?: string };
}

/**
 * The ordinary read: metadata only (DEC-A01-06). `available` says what exists;
 * the content comes from {@link retrieveDisputeEvidence}.
 */
export interface DisputeEvidence {
  dispute: {
    disputeId: string;
    enrollmentId: string;
    milestoneId: string;
    status: DisputeStatus;
    reason: string;
    openedAt: string;
    escalatedAt?: string;
    escalationNote?: string;
    resolution?: string;
    sla: SlaView;
  };
  enrollment: {
    enrollmentId: string;
    courseId: string;
    courseTitle?: string;
    parent: { userId: string; name?: string };
    teacher: { userId: string; name?: string };
  };
  milestone: {
    sequence: number;
    type: string;
    status: string;
    sessionState?: string;
    scheduledStart?: string;
    sessionStartedAt?: string;
    sessionEndedAt?: string;
    durationMinutes?: number;
    teacherCheckedInAt?: string;
    studentCheckedInAt?: string;
  };
  available: {
    rubric: boolean;
    deliverable: boolean;
    recording: boolean;
    teacherNotes: boolean;
  };
  milestoneHistory: Array<{
    sequence: number;
    type: string;
    status: string;
    completedAt?: string;
    releasedAt?: string;
  }>;
}

/** The protected half: VIEW_MINOR_DATA, fresh step-up, a reason and an audit row. */
export interface ProtectedDisputeEvidence {
  disputeId: string;
  childFirstName?: string;
  rubric?: { overall: number; summary: string };
  finalRubric?: { overall: number; summary: string };
  deliverable?: { signedUrl?: string };
  recording?: { recordingId?: string; signedUrl?: string };
  teacherNotes?: string;
}

export function getDisputeQueue(status?: DisputeStatus): Promise<{ disputes: DisputeQueueRow[] }> {
  const path = status ? `/api/v1/admin/disputes?status=${status}` : '/api/v1/admin/disputes';
  return apiGet(path);
}

export function getDisputeEvidence(disputeId: string): Promise<DisputeEvidence> {
  return apiGet(`/api/v1/disputes/${disputeId}/evidence`);
}

export function retrieveDisputeEvidence(
  disputeId: string,
  reason: string,
): Promise<ProtectedDisputeEvidence> {
  return apiPost(`/api/v1/disputes/${disputeId}/evidence/retrieve`, { reason });
}

export function escalateDispute(disputeId: string, note?: string): Promise<any> {
  return apiPost(`/api/v1/admin/disputes/${disputeId}/escalate`, { note });
}

export function resolveDispute(
  enrollmentId: string,
  disputeId: string,
  status: 'RESOLVED_RELEASED' | 'RESOLVED_REFUNDED',
  resolution: string,
): Promise<any> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/disputes/${disputeId}/resolve`, {
    status,
    resolution,
  });
}
